import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";

import { api } from "@/lib/api";
import { BackHeader } from "@/components/back-header";
import { Button, EmptyState, Loading } from "@/components/ui";
import { Modal } from "@/components/modal";

export function useAdminMe(enabled = true) {
  return useQuery({ queryKey: ["admin-me"], queryFn: () => api("/admin/me"), enabled });
}

export function AdminPage({ title, testId, right, children, back = true }) {
  return (
    <div data-testid={testId}>
      {back ? <BackHeader title={title} right={right} fallback="/admin" /> : null}
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

export function Gate({ perm, children, testId }) {
  const { data, isLoading } = useAdminMe();
  if (isLoading) return <Loading />;
  if (!data?.permissions.includes(perm)) return <EmptyState testId={testId} icon={Lock} title="No access" text="Your role doesn't include this section." />;
  return children;
}

export function ChipRow({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1">
      {options.map((o) => (
        <button key={o.id} data-testid={`${testIdPrefix}-${o.id || "all"}`} onClick={() => onChange(o.id)} aria-pressed={value === o.id}
          className={`h-9 shrink-0 rounded-full border px-4 text-sm font-medium transition-colors ${value === o.id ? "border-primary bg-primary text-white" : "border-border bg-white hover:bg-[#F6E3E4]"}`}>{o.label}</button>
      ))}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, testId, tone }) {
  return (
    <div className="rounded-[20px] border border-border bg-white p-4" data-testid={testId}>
      <Icon className={`h-[18px] w-[18px] ${tone === "gold" ? "text-[#C19B53]" : "text-primary"}`} />
      <p className="mt-2 font-display text-[28px] font-bold leading-none" data-testid={`${testId}-value`}>{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

export const StatGrid = ({ children }) => <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">{children}</div>;

export function Card({ children, testId, onClick, className = "" }) {
  const cls = `rounded-[20px] border border-border bg-white p-4 text-left ${className}`;
  if (!onClick) return <div className={cls} data-testid={testId}>{children}</div>;
  return <button data-testid={testId} onClick={onClick} className={`${cls} w-full transition-all hover:border-[#D48D93] hover:shadow-md`}>{children}</button>;
}

export function KV({ k, v, testId }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0" data-testid={testId}>
      <span className="capitalize text-muted-foreground">{k}</span><span className="text-right font-medium">{v ?? "—"}</span>
    </div>
  );
}

export const H = ({ children }) => <h2 className="mt-4 text-[24px]">{children}</h2>;
export const Meta = ({ children, testId }) => <p className="text-xs text-muted-foreground" data-testid={testId}>{children}</p>;

export function HBars({ data, testId }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-3 text-sm">
          <span className="w-32 truncate">{d.label}</span>
          <span className="h-3 flex-1 overflow-hidden rounded-full bg-[#F4EBE6]"><span className="block h-full rounded-full bg-[#D48D93] transition-all duration-700" style={{ width: `${(d.value / max) * 100}%` }} /></span>
          <span className="w-10 text-right font-bold">{d.value}</span>
        </div>
      ))}
    </div>
  );
}

export function ReasonDialog({ open, title, onClose, onConfirm, confirmLabel, testId, danger, placeholder, minLength = 3 }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Modal open={open} onClose={onClose} title={title} testId={testId}>
      <label htmlFor={`${testId}-input`} className="sr-only">Reason</label>
      <textarea id={`${testId}-input`} data-testid={`${testId}-input`} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder ?? "Reason (required)"}
        className="min-h-[110px] w-full rounded-xl border border-border p-3 text-[15px] focus:border-primary focus:outline-none" />
      <Button testId={`${testId}-confirm`} title={confirmLabel} variant={danger ? "danger" : "primary"} loading={busy} disabled={text.trim().length < minLength} className="mt-4 w-full"
        onClick={async () => { setBusy(true); try { await onConfirm(text.trim()); setText(""); } finally { setBusy(false); } }} />
    </Modal>
  );
}

export function Pager({ page, pages, total, onChange, testId }) {
  if (pages <= 1) return <Meta testId={`${testId}-total`}>{total} total</Meta>;
  return (
    <div className="flex items-center justify-between gap-3 py-2" data-testid={testId}>
      <Button testId={`${testId}-prev`} title="Previous" size="sm" variant="outline" icon={ChevronLeft} disabled={page <= 1} onClick={() => onChange(page - 1)} />
      <span className="text-sm font-medium" data-testid={`${testId}-label`}>Page {page} of {pages} · {total}</span>
      <Button testId={`${testId}-next`} size="sm" variant="outline" disabled={page >= pages} onClick={() => onChange(page + 1)}>Next <ChevronRight className="h-4 w-4" /></Button>
    </div>
  );
}

export const statusTone = (s) => (s === "active" ? "success" : s === "restricted" ? "gold" : s === "suspended" || s === "deactivated" ? "error" : "neutral");
export const reportTone = (s) => (s === "open" ? "error" : s === "in_review" ? "gold" : s === "resolved" ? "success" : "neutral");
